/**
 * Component for rendering GoogleMaps UI
 */

import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom'; // Import useLocation

import { Branch } from 'src/components/GoogleMaps/GoogleMapsTypes';

import { BranchLocationQueryContext } from '../StoreLocator/BranchLocationQuery';

import { googleMapsMarkerInfo } from './GoogleMapsMarkerInfo';
import { useGoogleMapsApi } from './GoogleMapsApiProvider';
import { MapTypeId, MapOptions } from './googleMapsApi';
import './styles/Map.scss';

interface GoogleMapProps {
    locations?: Branch[];
    height?: string;
    mapOptions: MapOptions;
}

const defaultGoogleMapProps: Pick<GoogleMapProps, 'mapOptions'> = {
    mapOptions: {
        mapTypeControl: false,
        streetViewControl: false,
        // Setting a default center might be better than 'earth' if fitBounds isn't always used
        center: { lat: 51.5074, lng: 0.1278 }, // Example: London
        mapTypeId: MapTypeId.ROADMAP,
        zoom: 16,
        initialCenter: null,
    },
};

export function GoogleMap(props: GoogleMapProps) {
    const { branches } = useContext(BranchLocationQueryContext);
    const branchesLoop = props.locations && props.locations?.length >= 1 ? props.locations : branches;

    const mapContainerRef = useRef<HTMLDivElement>(null); // Ref for the container div
    const mapInstanceRef = useRef<google.maps.Map | null>(null); // Ref to hold the map instance
    const markersRef = useRef<google.maps.Marker[]>([]); // Ref to hold markers
    const openInfoWindowRef = useRef<google.maps.InfoWindow | null>(null); // Ref for open info window

    const { apiIsReady, apiIsLoading, loadGoogleMapsApi } = useGoogleMapsApi();
    const location = useLocation(); // Get location object to detect route changes

    // Function to clear map elements (markers, info window)
    const clearMapElements = () => {
        if (openInfoWindowRef.current) {
            openInfoWindowRef.current.close();
            openInfoWindowRef.current = null;
        }
        markersRef.current.forEach((marker) => marker.setMap(null)); // Remove markers from map
        markersRef.current = []; // Clear the markers array
    };

    // Renamed loadMap to setupMap for clarity
    const setupMap = useCallback(() => {
        // Ensure container ref and Google Maps API are available
        if (!mapContainerRef.current || !window.google || !window.google.maps) {
            console.warn('Map setup prerequisites not met (container, google API).');
            return;
        }

        // --- Clean up previous map elements before creating/updating ---
        clearMapElements();

        const bounds = new google.maps.LatLngBounds();
        // --- MODIFIED: Determine view strategy ---
        const branchesCount = branchesLoop.length;
        const useCenterOnOne = branchesCount === 1; // Zoom in if exactly one branch
        const useFitBounds = branchesCount >= 2; // Use fitBounds if 2 or more branches
        // --- END MODIFICATION ---

        // Create map options, avoiding mutation of defaults
        const options: google.maps.MapOptions = {
            ...defaultGoogleMapProps.mapOptions,
            ...props.mapOptions,
        };

        // Configure view based on branches
        if (useCenterOnOne) {
            const { latitude, longitude } = branchesLoop[0];
            options.center = new google.maps.LatLng(latitude, longitude);
            // Use a specific zoom level for single markers, or fallback to props/default
            options.zoom = props.mapOptions.zoom ?? 16; // Example: Default zoom level 15 for single marker
            delete options.restriction; // Ensure fitBounds options are removed
        } else if (useFitBounds) {
            delete options.center;
            delete options.zoom;
        } else {
            // branchesCount === 0
            // No branches: Use default center/zoom or specific props
            if (typeof props.mapOptions.initialCenter === 'string') {
                if (props.mapOptions.initialCenter === 'earth') {
                    options.center = { lat: 0, lng: 0 };
                } else {
                    options.center = defaultGoogleMapProps.mapOptions.center;
                }
            } else {
                options.center = props.mapOptions.initialCenter ?? defaultGoogleMapProps.mapOptions.center;
            }
            options.zoom = props.mapOptions.zoom ?? defaultGoogleMapProps.mapOptions.zoom;
        }

        // --- Create a NEW map instance ---
        const map = new google.maps.Map(mapContainerRef.current, options);
        mapInstanceRef.current = map; // Store the new instance

        // Add markers for current branches
        branchesLoop.forEach((branch) => {
            const position = { lat: branch.latitude, lng: branch.longitude };
            const marker = new google.maps.Marker({
                position,
                map,
                title: branch.name,
                animation: google.maps.Animation.DROP,
            });

            const infoWindow = new google.maps.InfoWindow({
                content: googleMapsMarkerInfo(branch),
            });

            if (useFitBounds) {
                const pos = marker.getPosition();
                if (pos) {
                    bounds.extend(pos);
                }
            }

            // Add click listener for info window
            marker.addListener('click', () => {
                if (openInfoWindowRef.current) {
                    openInfoWindowRef.current.close();
                }
                infoWindow.open(map, marker);
                openInfoWindowRef.current = infoWindow;
            });

            markersRef.current.push(marker);
        });

        if (useFitBounds) {
            map.fitBounds(bounds);
        }
    }, [branchesLoop, props.mapOptions]); // Dependencies: Recreate setup function if data/options change

    // Effect to handle API loading and map setup/cleanup triggered by dependencies
    useEffect(() => {
        // Check if the API script needs loading based on state AND global object
        // This handles the case where provider state reset but script might still be loaded
        if (!apiIsReady && !window.google?.maps && !apiIsLoading) {
            // State says not ready, script not found globally, and not currently loading
            loadGoogleMapsApi();
        }
        // Check if we can proceed with setup
        else if (apiIsReady || window.google?.maps) {
            // State says ready OR the script is present globally (even if state is false)
            // Ensure the container is ready before setting up, might need a small delay
            // if mapContainerRef.current is null right after navigation
            if (mapContainerRef.current) {
                setupMap();
            } else {
                // If container isn't ready immediately, wait briefly.
                // This is a bit hacky, addressing the root cause (provider state) is better.
                console.warn('Map container not ready immediately after navigation, delaying setup slightly.');
                const timer = setTimeout(() => {
                    if (mapContainerRef.current) {
                        setupMap();
                    } else {
                        console.error('Map container still not ready after delay.');
                    }
                }, 100); // Small delay (e.g., 100ms)
                // Cleanup timer if effect re-runs or component unmounts before it fires
                return () => clearTimeout(timer);
            }
        }

        // --- Return Cleanup Function ---
        return () => {
            clearMapElements();
            mapInstanceRef.current = null;
            // No need to clear the timer here if it's defined above,
            // as the effect cleanup handles that specific timer instance.
        };
        // --- End Cleanup Function ---
    }, [apiIsReady, apiIsLoading, loadGoogleMapsApi, setupMap, location.pathname]);

    // Render the container div - ensure it has dimensions via CSS or style prop
    return (
        <div
            id="map"
            ref={mapContainerRef}
            style={{ height: props.height ?? '400px', minHeight: props.height ? '' : '50vh', width: '100%' }}
        />
    );
}

GoogleMap.defaultProps = defaultGoogleMapProps;

export default GoogleMap;
